import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import PhotoUpload from "./PhotoUpload";
import PhotoUploadProgressOverlay from "./PhotoUploadProgressOverlay";
import BarWeeklyHoursSection from "./BarWeeklyHoursSection";
import BarTimeSlotsConfigSection, {
  DEFAULT_SHOW_SLOT_TEMPLATE,
} from "./BarTimeSlotsConfigSection";
import { parseTimeSlots } from "../../../../../lib/barTimeSlots";
import {
  createDefaultWeeklyHours,
  weeklyHoursFromBar,
  weeklyHoursToApiPayload,
} from "../../../../utils/barWeeklyHours";
import { uploadPhotoFileWithGuards } from "../../../../services/uploadPhoto";
import { DEFAULT_BAR_TIME_ZONE } from "../../../../utils/civilDateTime.js";
import {
  US_TIME_ZONE_GROUPS,
  suggestUsIanaTimeZoneFromCoords,
} from "../../../../utils/usTimeZones.js";

function toPhotoItems(urls) {
  if (!Array.isArray(urls)) return [];
  return urls.map((url) => ({ type: "url", url }));
}

function photoItemsToUrls(items, newUrls) {
  let i = 0;
  return items.map((item) =>
    item.type === "url" ? item.url : newUrls[i++]
  );
}

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
  marginTop: "10px",
};

const defaultCenter = {
  lat: 24.5551,
  lng: -81.78,
};

const defaultFormState = {
  name: "",
  address: "",
  time_zone: DEFAULT_BAR_TIME_ZONE,
  latitude: null,
  longitude: null,
  weekly_hours: createDefaultWeeklyHours(),
  indoor_photos: [],
  outdoor_photos: [],
  time_slots_v2: {
    version: 2,
    enabled: true,
    defaultSlots: DEFAULT_SHOW_SLOT_TEMPLATE(),
    byDay: {},
  },
};

function mapSlotsForForm(slots) {
  if (!Array.isArray(slots) || slots.length === 0) return [];
  return slots.map((s, i) => ({
    id: s.id ?? i + 1,
    name: s.name || "",
    start_time: (s.start_time || "").slice(0, 5),
    end_time: (s.end_time || "").slice(0, 5),
  }));
}

function validateTimeSlotsV2(v2) {
  if (!v2 || v2.enabled === false) return null;
  const checkList = (arr, label) => {
    if (!Array.isArray(arr)) return null;
    for (let i = 0; i < arr.length; i++) {
      const s = arr[i];
      if (!s.name?.trim() || !s.start_time || !s.end_time) {
        return `Please complete all ${label} time slot fields (name, start, end).`;
      }
    }
    return null;
  };
  const defErr = checkList(v2.defaultSlots, "default");
  if (defErr) return defErr;
  const by = v2.byDay || {};
  for (const k of Object.keys(by)) {
    const list = by[k];
    if (Array.isArray(list) && list.length > 0) {
      const e = checkList(list, "per-day");
      if (e) return e;
    }
  }
  return null;
}

const EditBarModal = ({
  isOpen,
  onClose,
  bar,
  onSuccess,
  isMapsLoaded = false,
  overlayZIndex,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapZoom, setMapZoom] = useState(13);
  const [timeSlotErrors, setTimeSlotErrors] = useState({});
  const [barHoursError, setBarHoursError] = useState("");
  const [formError, setFormError] = useState("");
  const [formData, setFormData] = useState(defaultFormState);
  const [uploadOverlay, setUploadOverlay] = useState({
    visible: false,
    total: 0,
    current: -1,
    results: [],
  });
  const [rejectedOutdoorIndices, setRejectedOutdoorIndices] = useState([]);
  const [rejectedIndoorIndices, setRejectedIndoorIndices] = useState([]);
  const uploadAbortRef = useRef(null);

  const parseBarField = (val, defaultVal) => {
    if (val == null) return defaultVal;
    if (typeof val === "string") {
      try {
        return JSON.parse(val) || defaultVal;
      } catch {
        return defaultVal;
      }
    }
    return val;
  };

  useEffect(() => {
    if (isOpen && bar) {
      const indoor = parseBarField(bar.indoor_photos, { photos: [] });
      const outdoor = parseBarField(bar.outdoor_photos, { photos: [] });
      const lat = bar.latitude != null ? parseFloat(bar.latitude) : null;
      const lng = bar.longitude != null ? parseFloat(bar.longitude) : null;
      const p = parseTimeSlots(bar.time_slots);
      const time_slots_v2 = {
        version: 2,
        enabled: p.enabled,
        defaultSlots: mapSlotsForForm(
          p.defaultSlots?.length ? p.defaultSlots : DEFAULT_SHOW_SLOT_TEMPLATE()
        ),
        byDay: Object.fromEntries(
          Object.entries(p.byDay || {}).map(([k, v]) => {
            if (v === null || v === undefined) return [k, null];
            if (Array.isArray(v) && v.length === 0) return [k, []];
            return [k, mapSlotsForForm(v)];
          })
        ),
      };

      setFormData({
        name: bar.name || "",
        address: bar.address || "",
        time_zone: bar.time_zone || DEFAULT_BAR_TIME_ZONE,
        latitude: lat,
        longitude: lng,
        weekly_hours: weeklyHoursFromBar(bar),
        indoor_photos: toPhotoItems(indoor.photos),
        outdoor_photos: toPhotoItems(outdoor.photos),
        time_slots_v2,
      });

      if (
        lat != null &&
        lng != null &&
        !isNaN(lat) &&
        !isNaN(lng) &&
        (lat !== 0 || lng !== 0)
      ) {
        const center = { lat, lng };
        setMapCenter(center);
        setMarkerPosition(center);
        setMapZoom(14);
      } else {
        setMapCenter(defaultCenter);
        setMarkerPosition(null);
        setMapZoom(13);
      }
    }
  }, [isOpen, bar]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(defaultFormState);
      setMapCenter(defaultCenter);
      setMarkerPosition(null);
      setMapZoom(13);
      setAutocomplete(null);
      setTimeSlotErrors({});
      setBarHoursError("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onLoadAutocomplete = (autocompleteObj) => {
    setAutocomplete(autocompleteObj);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setFormData({
          ...formData,
          address: place.formatted_address || formData.address,
          latitude: lat,
          longitude: lng,
        });
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setMapZoom(18);
      }
    }
  };

  const onMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setFormData({ ...formData, latitude: lat, longitude: lng });
    setMarkerPosition({ lat, lng });
  };

  const handleIndoorPhotosChange = (items) => {
    setRejectedIndoorIndices([]);
    setFormData({ ...formData, indoor_photos: items });
  };

  const handleOutdoorPhotosChange = (items) => {
    setRejectedOutdoorIndices([]);
    setFormData({ ...formData, outdoor_photos: items });
  };

  const handleRemoveOutdoor = async (index) => {
    const list = formData.outdoor_photos;
    if (list.length <= 1) {
      setFormError(
        "You need at least one outdoor photo. Add a new one first, then you can remove this one."
      );
      return;
    }
    const item = list[index];
    if (item.type === "url") {
      try {
        const res = await fetch("/api/upload/photo", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ url: item.url }),
        });
        const data = await res.json();
        if (!data.success) {
          setFormError(data.error || "Could not delete photo. Please try again.");
          return;
        }
      } catch (err) {
        setFormError("Could not delete photo. Please try again.");
        return;
      }
    }
    const updated = list.filter((_, i) => i !== index);
    setRejectedOutdoorIndices((prev) =>
          prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
        );
    setFormData({ ...formData, outdoor_photos: updated });
    setFormError("");
  };

  const handleRemoveIndoor = async (index) => {
    const list = formData.indoor_photos;
    const item = list[index];
    if (item.type === "url") {
      try {
        const res = await fetch("/api/upload/photo", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ url: item.url }),
        });
        const data = await res.json();
        if (!data.success) {
          setFormError(data.error || "Could not delete photo. Please try again.");
          return;
        }
      } catch (err) {
        setFormError("Could not delete photo. Please try again.");
        return;
      }
    }
    const updated = list.filter((_, i) => i !== index);
    setRejectedIndoorIndices((prev) =>
          prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
        );
    setFormData({ ...formData, indoor_photos: updated });
    setFormError("");
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!formData.name.trim()) {
      setFormError("Please enter a bar name.");
      return;
    }
    if (!formData.address.trim()) {
      setFormError("Please enter an address.");
      return;
    }
    if (!formData.latitude || !formData.longitude) {
      setFormError(
        "Please select a valid address from the dropdown suggestions to set location."
      );
      return;
    }
    const outdoorCount = formData.outdoor_photos.length;
    if (outdoorCount < 1) {
      setFormError("Please add at least one outdoor photo.");
      return;
    }
    if (barHoursError) {
      setFormError(barHoursError);
      return;
    }
    const slotMsg = validateTimeSlotsV2(formData.time_slots_v2);
    if (slotMsg) {
      setFormError(slotMsg);
      return;
    }
    const hasTimeSlotValidationErrors = Object.values(timeSlotErrors).some(
      (err) => err != null && err !== ""
    );
    if (hasTimeSlotValidationErrors) {
      setFormError("Please fix time slot validation errors before submitting.");
      return;
    }

    const outdoorFiles = formData.outdoor_photos.filter((i) => i.type === "file");
    const indoorFiles = formData.indoor_photos.filter((i) => i.type === "file");
    const totalFiles = outdoorFiles.length + indoorFiles.length;

    let finalOutdoorUrls = [];
    let finalIndoorUrls = [];

    if (totalFiles > 0) {
      uploadAbortRef.current = new AbortController();
      setUploadOverlay({
        visible: true,
        total: totalFiles,
        current: -1,
        results: [],
      });
      const results = [];
      const outdoorUrls = [];
      const indoorUrls = [];

      try {
        for (let i = 0; i < outdoorFiles.length; i++) {
          let data;
          try {
            data = await uploadPhotoFileWithGuards(outdoorFiles[i].file, {
              batchSignal: uploadAbortRef.current.signal,
            });
          } catch (uploadErr) {
            if (uploadErr.code === "CANCELLED") {
              setFormError("Upload cancelled.");
            } else if (uploadErr.code === "TIMEOUT") {
              setFormError(uploadErr.message);
            } else {
              setFormError(
                uploadErr.message ||
                  "Something went wrong uploading photos. Please try again."
              );
            }
            return;
          }
          const ok = data.ok && data.url;
          results.push(ok ? "success" : "rejected");
          if (ok) outdoorUrls.push(data.url);
          setUploadOverlay((prev) => ({
            ...prev,
            current: i,
            results: [...results],
          }));
        }
        for (let i = 0; i < indoorFiles.length; i++) {
          let data;
          try {
            data = await uploadPhotoFileWithGuards(indoorFiles[i].file, {
              batchSignal: uploadAbortRef.current.signal,
            });
          } catch (uploadErr) {
            if (uploadErr.code === "CANCELLED") {
              setFormError("Upload cancelled.");
            } else if (uploadErr.code === "TIMEOUT") {
              setFormError(uploadErr.message);
            } else {
              setFormError(
                uploadErr.message ||
                  "Something went wrong uploading photos. Please try again."
              );
            }
            return;
          }
          const ok = data.ok && data.url;
          results.push(ok ? "success" : "rejected");
          if (ok) indoorUrls.push(data.url);
          setUploadOverlay((prev) => ({
            ...prev,
            current: outdoorFiles.length + i,
            results: [...results],
          }));
        }
      } finally {
        setUploadOverlay((prev) => ({ ...prev, visible: false }));
        uploadAbortRef.current = null;
      }

      const rejectedOneBased = results
        .map((r, i) => (r === "rejected" ? i + 1 : null))
        .filter((x) => x != null);
      if (rejectedOneBased.length > 0) {
        const outdoorFormIndices = [];
        let fi = 0;
        let outdoorUrlIdx = 0;
        let indoorUrlIdx = 0;
        const newOutdoorPhotos = formData.outdoor_photos.map((item) => {
          if (item.type === "url") return item;
          const r = results[fi++];
          if (r === "success") {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            return { type: "url", url: outdoorUrls[outdoorUrlIdx++] };
          }
          return item;
        });
        const newIndoorPhotos = formData.indoor_photos.map((item) => {
          if (item.type === "url") return item;
          const r = results[fi++];
          if (r === "success") {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            return { type: "url", url: indoorUrls[indoorUrlIdx++] };
          }
          return item;
        });
        setFormData({
          ...formData,
          outdoor_photos: newOutdoorPhotos,
          indoor_photos: newIndoorPhotos,
        });
        fi = 0;
        for (let i = 0; i < formData.outdoor_photos.length; i++) {
          if (formData.outdoor_photos[i].type === "file") {
            if (results[fi] === "rejected") outdoorFormIndices.push(i);
            fi++;
          }
        }
        const indoorFormIndices = [];
        for (let i = 0; i < formData.indoor_photos.length; i++) {
          if (formData.indoor_photos[i].type === "file") {
            if (results[fi] === "rejected") indoorFormIndices.push(i);
            fi++;
          }
        }
        setRejectedOutdoorIndices(outdoorFormIndices);
        setRejectedIndoorIndices(indoorFormIndices);
        setFormError(
          `Photo${rejectedOneBased.length > 1 ? "s" : ""} ${rejectedOneBased.join(" and ")} ${rejectedOneBased.length > 1 ? "were" : "was"} rejected. Please remove or replace ${rejectedOneBased.length > 1 ? "them" : "it"}.`
        );
        return;
      }

      finalOutdoorUrls = photoItemsToUrls(formData.outdoor_photos, outdoorUrls);
      finalIndoorUrls = photoItemsToUrls(formData.indoor_photos, indoorUrls);
    } else {
      finalOutdoorUrls = formData.outdoor_photos.map((i) => i.url);
      finalIndoorUrls = formData.indoor_photos.map((i) => i.url);
    }

    setIsSubmitting(true);
    const barData = {
      name: formData.name.trim(),
      address: formData.address.trim(),
      time_zone: formData.time_zone,
      latitude: formData.latitude,
      longitude: formData.longitude,
      weekly_hours: weeklyHoursToApiPayload(formData.weekly_hours),
      indoor_photos: { photos: finalIndoorUrls },
      outdoor_photos: { photos: finalOutdoorUrls },
      time_slots: formData.time_slots_v2,
    };

    try {
      const url = bar ? `/api/bars/${bar.bar_id}` : "/api/bars";
      const method = bar ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(barData),
      });
      const data = await response.json();

      if (data.success) {
        if (onSuccess) onSuccess(data.bar || { ...bar, ...barData });
        handleClose();
      } else {
        setFormError(data.error || "Failed to save bar. Please try again.");
      }
    } catch (err) {
      console.error("Save bar error:", err);
      setFormError("Failed to save bar. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <PhotoUploadProgressOverlay
        visible={uploadOverlay.visible}
        total={uploadOverlay.total}
        current={uploadOverlay.current}
        results={uploadOverlay.results}
        onCancel={
          uploadOverlay.visible
            ? () => uploadAbortRef.current?.abort()
            : undefined
        }
      />
    <div
      className="artist-profile-modal-overlay"
      role="presentation"
      style={overlayZIndex != null ? { zIndex: overlayZIndex } : undefined}
    >
      <div
        className="artist-profile-modal groups-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-bar-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "900px", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="artist-profile-modal-header">
          <h2 id="edit-bar-modal-title">{bar ? "Edit Bar" : "Add New Bar"}</h2>
          <button
            type="button"
            className="artist-profile-modal-close"
            aria-label="Close"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="artist-profile-modal-body groups-modal-body">
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            className="onboarding-form"
          >
            <div className="onboarding-section">
              <h2 className="section-title">Bar Information</h2>

              <div className="form-grid">
                <div className="input-group full-width">
                  <label className="input-label">Bar Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="settings-input"
                    placeholder="Enter bar name"
                    required
                    maxLength={128}
                    disabled={isSubmitting}
                  />
                </div>

                <div
                  className="input-group full-width"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="input-label">Address *</label>
                  {isMapsLoaded ? (
                    <Autocomplete
                      onLoad={onLoadAutocomplete}
                      onPlaceChanged={onPlaceChanged}
                    >
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="settings-input"
                        placeholder="Start typing address..."
                        required
                        maxLength={255}
                        disabled={isSubmitting}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="settings-input"
                      placeholder="Loading map..."
                      disabled
                    />
                  )}
                  <p className="input-hint">
                    Select from dropdown suggestions, then adjust the map marker
                    if needed
                  </p>
                </div>

                <div
                  className="input-group full-width"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="input-label" htmlFor="edit-bar-time-zone">
                    Venue timezone
                  </label>
                  <select
                    id="edit-bar-time-zone"
                    name="time_zone"
                    value={formData.time_zone}
                    onChange={handleInputChange}
                    className="settings-input"
                    disabled={isSubmitting}
                    aria-label="Venue IANA time zone for scheduling"
                  >
                    {US_TIME_ZONE_GROUPS.map((group) => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <div className="form-actions" style={{ marginTop: 8, gap: 8 }}>
                    <button
                      type="button"
                      className="cancel-btn"
                      style={{ padding: "6px 12px", fontSize: "0.9rem" }}
                      disabled={
                        isSubmitting ||
                        formData.latitude == null ||
                        formData.longitude == null
                      }
                      onClick={() => {
                        const guess = suggestUsIanaTimeZoneFromCoords(
                          formData.latitude,
                          formData.longitude
                        );
                        if (guess) {
                          setFormData({ ...formData, time_zone: guess });
                        }
                      }}
                    >
                      Suggest from map pin
                    </button>
                  </div>
                  <p className="input-hint">
                    Show dates follow this zone. The suggestion uses rough US
                    longitude bands — adjust if your venue is near a border.
                  </p>
                </div>

                <div
                  className="input-group full-width"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="input-label">
                    {formData.latitude && formData.longitude
                      ? "Finalize Location (Drag Marker)"
                      : "Select an address to place marker"}
                  </label>
                  {isMapsLoaded ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={mapCenter}
                        zoom={mapZoom}
                        options={{ gestureHandling: "cooperative" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formData.latitude && formData.longitude && (
                          <Marker
                            position={
                              markerPosition || {
                                lat: formData.latitude,
                                lng: formData.longitude,
                              }
                            }
                            draggable
                            onDragEnd={onMarkerDragEnd}
                          />
                        )}
                      </GoogleMap>
                    </div>
                  ) : (
                    <div
                      style={{
                        ...mapContainerStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f0f0f0",
                        color: "#666",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Loading map...
                    </div>
                  )}
                  {formData.latitude && formData.longitude && (
                    <p className="input-hint">
                      Coordinates: {formData.latitude.toFixed(6)},{" "}
                      {formData.longitude.toFixed(6)}
                    </p>
                  )}
                </div>

                <div className="input-group full-width">
                  <BarWeeklyHoursSection
                    value={formData.weekly_hours}
                    onChange={(next) =>
                      setFormData({ ...formData, weekly_hours: next })
                    }
                    disabled={isSubmitting}
                    onValidationChange={setBarHoursError}
                  />
                </div>
              </div>
            </div>

            <div className="onboarding-section">
              <h2 className="section-title">Bar Photos</h2>
              <PhotoUpload
                label="Outdoor Photos"
                value={formData.outdoor_photos}
                onChange={handleOutdoorPhotosChange}
                onRemove={handleRemoveOutdoor}
                minPhotos={1}
                maxPhotos={3}
                rejectedIndices={rejectedOutdoorIndices}
              />
              <PhotoUpload
                label="Indoor Photos"
                value={formData.indoor_photos}
                onChange={handleIndoorPhotosChange}
                onRemove={handleRemoveIndoor}
                minPhotos={0}
                maxPhotos={3}
                rejectedIndices={rejectedIndoorIndices}
              />
            </div>

            <div className="onboarding-section">
              <h2 className="section-title">Show Time Slots</h2>
              <BarTimeSlotsConfigSection
                value={formData.time_slots_v2}
                onChange={(time_slots_v2) =>
                  setFormData({ ...formData, time_slots_v2 })
                }
                disabled={isSubmitting}
                onValidationChange={setTimeSlotErrors}
              />
            </div>

            {formError && (
              <div className="error-message" role="alert">
                <p>{formError}</p>
              </div>
            )}
            <div className="onboarding-actions" style={{ marginTop: "1rem" }}>
              <button
                type="button"
                className="modal-btn secondary"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submit-btn"
                disabled={isSubmitting || !formData.latitude}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
              >
                {isSubmitting
                  ? bar
                    ? "Saving..."
                    : "Adding..."
                  : bar
                  ? "Update Bar"
                  : "Add Bar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default EditBarModal;
